import React from 'react';
import { Box, List, ListItem, ListItemText, IconButton, Typography, useTheme, Drawer } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import AddIcon from '@mui/icons-material/Add';
import { format, isToday, isYesterday, differenceInDays } from 'date-fns';
import Logo from './Logo';
import { Link } from 'react-router-dom';
import ThreadMenu from './ThreadMenu';

export const EXPANDED_WIDTH = 250;
export const COLLAPSED_WIDTH = 60;

const ThreadsSidebar = ({ 
    threads, 
    currentThreadId, 
    onThreadSelect, 
    onThreadDelete,
    onThreadRename,
    isDarkMode,
    isOpen,
    onToggle,
    onNewThread
}) => {
    const theme = useTheme();

    // Group threads by date
    const groupThreadsByDate = (threads) => {
        const groups = {};
        threads.forEach(thread => {
            const date = new Date(thread.updated_at?.seconds * 1000 || Date.now());
            let groupKey;
            
            if (isToday(date)) {
                groupKey = 'Today';
            } else if (isYesterday(date)) {
                groupKey = 'Yesterday';
            } else {
                const daysDifference = differenceInDays(new Date(), date);
                
                if (daysDifference <= 7) {
                    groupKey = '7 days ago';
                } else if (daysDifference <= 14) {
                    groupKey = '2 weeks ago';
                } else if (daysDifference <= 30) {
                    groupKey = '1 month ago';
                } else {
                    groupKey = format(date, 'MMMM d, yyyy');
                }
            }
            
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(thread);
        });
        return groups;
    };

    const groupedThreads = groupThreadsByDate(threads);

    return (
        <Drawer
            variant="permanent"
            anchor="left"
            sx={{
                width: isOpen ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
                flexShrink: 0,
                position: 'absolute',
                zIndex: theme.zIndex.drawer,
                height: '100%',
                '& .MuiDrawer-paper': {
                    position: 'fixed',
                    width: isOpen ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
                    height: '100vh',
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    boxSizing: 'border-box',
                    backgroundColor: isDarkMode ? 'background.paper' : 'background.default',
                    borderRight: 1,
                    borderColor: 'divider',
                    overflowX: 'hidden',
                    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                        boxShadow: isOpen ? theme.shadows[8] : 'none',
                        zIndex: isOpen ? theme.zIndex.modal : theme.zIndex.drawer,
                        position: 'fixed',
                        height: '100%',
                        width: isOpen ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
                    },
                },
                [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                    width: isOpen ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
                    position: 'absolute',
                    height: '100vh',
                },
            }}
        >
            {/* Logo and Toggle Section */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                {/* Logo Container */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isOpen ? 'space-between' : 'center',
                        p: 2,
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                        <Link 
                            to="/" 
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                textDecoration: 'none'
                            }}
                        >
                            <Logo width={isOpen ? "48" : "36"} height={isOpen ? "48" : "36"} />
                            {isOpen && (
                                <Typography
                                    variant="h6"
                                    sx={{
                                        ml: 1,
                                        fontSize: '1.2rem',
                                        fontWeight: 500,
                                        color: 'text.primary',
                                    }}
                                >
                                    calmind.ai
                                </Typography>
                            )}
                        </Link>
                    </Box>
                    {/* Toggle Button - Only show in expanded state */}
                    {isOpen && (
                        <IconButton 
                            onClick={onToggle}
                            sx={{
                                transform: 'rotate(0deg)',
                                transition: theme.transitions.create('transform', {
                                    duration: theme.transitions.duration.shorter,
                                }),
                            }}
                        >
                            <ViewSidebarIcon />
                        </IconButton>
                    )}
                </Box>
                
                {/* Toggle Button - Only show in collapsed state */}
                {!isOpen && (
                    <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
                        <IconButton 
                            onClick={onToggle}
                            sx={{
                                transform: 'rotate(180deg)',
                                transition: theme.transitions.create('transform', {
                                    duration: theme.transitions.duration.shorter,
                                }),
                            }}
                        >
                            <ViewSidebarIcon />
                        </IconButton>
                    </Box>
                )}
            </Box>

            {/* Conversations Header */}
            {isOpen ? (
                <Box
                    sx={{
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Conversations
                    </Typography>
                    <IconButton 
                        onClick={onNewThread}
                        size="small"
                    >
                        <AddIcon />
                    </IconButton>
                </Box>
            ) : (
                <Box
                    sx={{
                        p: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    <IconButton 
                        onClick={onNewThread}
                        size="small"
                    >
                        <AddIcon />
                    </IconButton>
                </Box>
            )}

            {/* Threads List */}
            <List 
                sx={{ 
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '3px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        },
                    },
                }}
            >
                {Object.entries(groupedThreads).map(([date, dateThreads]) => (
                    <Box key={date}>
                        {isOpen && (
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    px: 2,
                                    py: 1,
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                }}
                            >
                                {date}
                            </Typography>
                        )}
                        {dateThreads.map((thread) => (
                            <ListItem
                                key={thread.id}
                                selected={currentThreadId === thread.id}
                                onClick={() => onThreadSelect(thread.id)}
                                sx={{
                                    cursor: 'pointer',
                                    px: isOpen ? 2 : 1,
                                    minHeight: 48,
                                    justifyContent: 'center',
                                    display: isOpen ? 'flex' : 'none',
                                    '&:hover': {
                                        backgroundColor: isDarkMode 
                                            ? 'rgba(255, 255, 255, 0.08)'
                                            : 'rgba(0, 0, 0, 0.04)',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: isDarkMode 
                                            ? 'rgba(255, 255, 255, 0.16)'
                                            : theme.palette.primary.light,
                                    },
                                }}
                                secondaryAction={
                                    <ThreadMenu 
                                        threadId={thread.id}
                                        onDelete={onThreadDelete}
                                        onRename={onThreadRename || ((id) => console.log(`Rename thread ${id} - Functionality not implemented`))}
                                    />
                                }
                            >
                                <ListItemText
                                    primary={thread.title}
                                    primaryTypographyProps={{
                                        noWrap: true,
                                        style: { maxWidth: '170px' }
                                    }}
                                />
                            </ListItem>
                        ))}
                    </Box>
                ))}
            </List>
        </Drawer>
    );
};

export default ThreadsSidebar; 